import {
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useMembershipDisplayConfig } from '@/hooks/useMembershipDisplayConfig';
import { Card } from '@/imports/ui/chakra/components/Card';
import { DefaultNoSearchResults } from '@/imports/ui/components/DefaultNoSearchResults';
import { useSelectionState } from '@/imports/ui/useSelectionState';

import { IUseMembersPagination } from '../hooks/useMembersPagination/useMembersPagination';
import { AccountStatus } from './components/AccountStatus';
import { Member } from './components/Member';
import { Member_UserFragment } from './components/Member/graphql-types/Member_UserFragment';
import { MemberPlaceholders } from './components/MemberPlaceholders';
import { SelectionDescription } from './components/SelectionDescription';
import { BulkImportButton as MembersBulkImportButton } from '../components/BulkImportButton';
import { BulkArchiveCsvButton } from '../components/BulkArchiveCsvButton';
import { BulkArchiveOrganizationsCsvButton } from '../components/BulkArchiveOrganizationsCsvButton';
import { BulkImportButton as OrganizationsBulkImportButton } from '@/components/admin/sponsorships/Sponsors/components/BulkImportButton/BulkImportButton';
import { OrganizationResult } from '../hooks/useOrganizationsPagination/useOrganizationsPagination';
import {
  FilterIcon,
  PlusIcon,
} from '@/imports/ui/chakra/feather';
import NextLink from 'next/link';
import { ContactFilter } from './components/ContactFilter';
import { useOrganizationsPagination } from '../hooks/useOrganizationsPagination/useOrganizationsPagination';
import { OrganizationRow } from './components/OrganizationRow/OrganizationRow';
import { ContactFilterValue } from './components/ContactFilter/contactFilter';
import { CombinedSearchRow } from './components/CombinedSearchRow/CombinedSearchRow';
import { SearchBox } from '@/components/SearchBox';
import { AddContactModal } from '@/components/admin/AddContactModal';
import { AddContactModalValues } from '@/components/admin/AddContactModal/AddContactModal';

import { MembershipStatusFilter } from '@/components/MembershipStatusFilter';
import { MembershipType } from './components/MembershipType';
import { MembershipTypeFilter } from '@/components/MembershipTypeFilter';
import { TermAlertsFilter } from '@/components/TermAlertsFilter';
import { SelectButton } from '@/components/SelectButton';
import { Role } from './components/Role';
import { OrganizationSearchInput_Filter } from '@/graphql-types/globalTypes';
import { UserSearchExportButton } from '../components/UserSearchExportButton';
import { useDownloadUserCsvErrors } from '@/imports/ui/hooks/useDownloadErrorFile/useDownloadUserCsvErrors';
import { OrganizationSearchExportButton } from '@/components/admin/sponsorships/Sponsors/components/OrganizationSearchExportButton';
import { useDownloadCompaniesCsvErrors } from '@/imports/ui/hooks/useDownloadErrorFile/useDownloadCompaniesCsvErrors';
import { BulkInviteButton } from './components/BulkInviteButton';
import { NewAddContactModalMember } from '@/components/admin/NewAddContactModalMember';

import { useRouter } from 'next/router';


enum COMPANY_TYPE_FILTER {
  ALL = 'All',
  SPONSOR = 'Sponsor',
  RECORD = 'Record',
}

enum COMPANY_STATUS_FILTER {
  ALL = 'All',
  HIDDEN = 'Hidden',
  ACTIVE = 'Active',
}

type OrganizationSearchInputReducerAction = {
  limit?: number;
  offset?: number;
  filter?: OrganizationSearchInput_Filter;
};

type IMembersRecordList = IUseMembersPagination & {
  onOpenDeactivatedLogs?: () => void;
  onOpenArchivedOrganizationLogs?: () => void;
};

const getRowId = (
    type: 'member' | 'organization',
    id: string
) => `${type}-${id}`;

export const MembersRecordList: React.FC<IMembersRecordList> = (props) => {
  const memberManagement = useMemberManagement();
  // Gate table until display config is known — avoids flash of Past Due / Ex-Member then hide
  const { loading: membershipDisplayConfigLoading } =
    useMembershipDisplayConfig();
  const router = useRouter();
  const {
    userSearchResults,
    dispatch,
    queryState,
    isLoading,
    isCountLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    limit,
    totalCount: membersTotalCount,
    archivedOnly = false,
    onOpenDeactivatedLogs,
    onOpenArchivedOrganizationLogs,
  } = props;

  const [contactFilter, setContactFilter] =
  React.useState<ContactFilterValue>('individual');

  React.useEffect(() => {
  if (router.query.contactType === 'organization') {
    setContactFilter('organization');
  } else if (router.query.contactType === 'individual') {
    setContactFilter('individual');
  }
}, [router.query.contactType]);

  const [input, setInput] = React.useReducer(
    (
      prevState: {
        limit: number;
        offset: number;
        filter: OrganizationSearchInput_Filter;
      },
      action: OrganizationSearchInputReducerAction
    ) => ({
      ...prevState,
      ...action,
      filter: {
        ...prevState.filter,
        ...action.filter,
      },
    }),
    {
      limit: 20,
      offset: 0,
      filter: {
        searchText: queryState.searchText || undefined,
        hidden: archivedOnly ? true : false,
      },
    }
  );

  React.useEffect(() => {
    setInput({
      filter: {
        searchText: queryState.searchText || undefined,
        hidden: archivedOnly ? true : false,
      },
      offset: 0,
    });
  }, [queryState.searchText, archivedOnly]);

  const shouldFetchOrganizations =
    contactFilter === 'organization' ||
    !!(queryState.searchText && queryState.searchText.length > 0);

  const orgPagination = useOrganizationsPagination(
    queryState.searchText ?? '',
    input.filter,
    { enabled: shouldFetchOrganizations }
  );

  const {
    selection,
    selectAll,
    selectNone,
    selectMany,
    toggleOne,
  } = useSelectionState<Member_UserFragment>(
    (item: Member_UserFragment) => item._id
  );

  const members = React.useMemo(
    () => userSearchResults?.data?.userSearch?.results || [],
    [userSearchResults]
  );
  const organizations = React.useMemo(
    () => orgPagination.organizations,
    [orgPagination.organizations]
  );

  const isIndividualView = contactFilter === 'individual';
  const isOrganizationView = contactFilter === 'organization';
  const isCombinedView = queryState.searchText && queryState.searchText.length > 0;

  const addContactModal = useDisclosure();

  const visibleMembers = React.useMemo(
    () => (isIndividualView ? members : []),
    [isIndividualView, members]
  );
  const visibleOrganizations = React.useMemo(
    () => (isOrganizationView ? organizations : []),
    [isOrganizationView, organizations]
  );
  
  const combinedResults = React.useMemo(() => {
    if (!isCombinedView) return [];
    const memberResults = members.map(m => ({ type: 'member' as const, data: m }));
    const orgResults = organizations.map(o => ({ type: 'organization' as const, data: o }));
    return [...memberResults, ...orgResults];
  }, [isCombinedView, members, organizations]);
  const displayTotalCount = isCombinedView
    ? combinedResults.length
    : isIndividualView
    ? isCountLoading
      ? visibleMembers.length
      : membersTotalCount
    : orgPagination.totalCount ?? visibleOrganizations.length;

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = React.useRef(false);
  const {
    hasMore: orgHasMore,
    isLoading: orgIsLoading,
    isFetchingMore: orgIsFetchingMore,
    loadMore: orgLoadMore,
  } = orgPagination;

  React.useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (!firstEntry?.isIntersecting || isLoadingMoreRef.current) return;

        const loadNextPage = async () => {
          if (isLoadingMoreRef.current) return;
          isLoadingMoreRef.current = true;

          try {
            if (isCombinedView) {
              if (hasMore && !isLoading && !isFetchingMore) {
                await loadMore();
              }
              if (orgHasMore && !orgIsLoading && !orgIsFetchingMore) {
                await orgLoadMore();
              }
            } else if (isIndividualView) {
              if (hasMore && !isLoading && !isFetchingMore && !isCountLoading) {
                await loadMore();
              }
            } else if (isOrganizationView) {
              if (orgHasMore && !orgIsLoading && !orgIsFetchingMore) {
                await orgLoadMore();
              }
            }
          } finally {
            isLoadingMoreRef.current = false;
          }
        };

        void loadNextPage();
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [
    isCombinedView,
    isIndividualView,
    isOrganizationView,
    hasMore,
    isLoading,
    isFetchingMore,
    loadMore,
    orgHasMore,
    orgIsLoading,
    orgIsFetchingMore,
    orgLoadMore,
    isCountLoading,
  ]);

  const {
    selection: orgSelection,
    selectAll: orgSelectAll,
    selectNone: orgSelectNone,
    selectMany: orgSelectMany,
    toggleOne: orgToggleOne,
  } = useSelectionState<OrganizationResult>(
    (item: OrganizationResult) => item._id
  );

  const allOrganizationsOnPageSelected =
    visibleOrganizations.length > 0 &&
    visibleOrganizations.every((org) => {
      if (orgSelection.allSelected) {
        return !orgSelection.excludedItems.map(item => item._id).includes(org._id);
      } else {
        return orgSelection.includedItems.map(item => item._id).includes(org._id);
      }
    });

  const someOrganizationsSelected = visibleOrganizations.some((org) => {
    if (orgSelection.allSelected) {
      return !orgSelection.excludedItems.map(item => item._id).includes(org._id);
    } else {
      return orgSelection.includedItems.map(item => item._id).includes(org._id);
    }
  });

  const headerIsChecked = isCombinedView
    ? false
    : isIndividualView
    ? selection.allSelected
    : orgSelection.allSelected;

  const headerIsIndeterminate = isCombinedView
    ? false
    : isIndividualView
    ? ((!selection.allSelected && selection.includedItems.length > 0) ||
        (selection.allSelected && selection.excludedItems.length > 0))
    : ((!orgSelection.allSelected && orgSelection.includedItems.length > 0) ||
        (orgSelection.allSelected && orgSelection.excludedItems.length > 0));

  const handleSelectAllChange = () => {
    if (isCombinedView) {
      return; // Disable selection for combined view
    }
    
    if (isIndividualView) {
      const shouldSelectAll = !selection.allSelected;
      if (shouldSelectAll) {
        selectAll();
      } else {
        selectNone();
      }
      return;
    }

    const shouldSelectAll = !orgSelection.allSelected;
    if (shouldSelectAll) {
      orgSelectAll();
    } else {
      orgSelectNone();
    }
  };

  const [showFilters, setShowFilters] = React.useState(false);
  

  // Include display-config loading so Membership Status never flashes wrong badges
  const isCurrentLoading =
    membershipDisplayConfigLoading ||
    (isCombinedView
      ? isLoading || orgPagination.isLoading
      : isIndividualView
        ? isLoading
        : orgPagination.isLoading);
  const isCurrentFetchingMore = isCombinedView
    ? (isFetchingMore || orgPagination.isFetchingMore)
    : isIndividualView
    ? isFetchingMore
    : orgPagination.isFetchingMore;
  const hasCurrentMore = isCombinedView
    ? (hasMore || orgPagination.hasMore)
    : isIndividualView ? hasMore : orgPagination.hasMore;

  // export error handling
  useDownloadUserCsvErrors({ validRoutes: ['people'] });
  useDownloadCompaniesCsvErrors({ validRoutes: ['companies'] });

  const organizationNumPages = Math.ceil(
    (orgPagination.totalCount ?? visibleOrganizations.length) / limit
  );


const selectAllOrganizations = () => {
  orgSelectAll();
};

const selectOnlyOrganizationsOnPage = () => {
  orgSelectMany(visibleOrganizations, false);
};
  return (
    <Stack spacing={archivedOnly ? '3' : '4'}>
      <Card p="0" bg="transparent" border="0" boxShadow="none">
        <Stack spacing={archivedOnly ? '3' : '4'}>
          <Stack spacing="2" direction="row" w="100%">
              <Box maxW="400px" w="100%">
                <SearchBox
                  placeholder="Search names or emails"
                  onChange={(val) => {
                    if (val !== queryState.searchText) {
                      dispatch({
                        searchText: val,
                      });
                    }
                  }}
                  value={queryState.searchText}
                />
              </Box>
               {!isCombinedView && !archivedOnly && (
              <Button
                variant="ghost"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <FilterIcon />
              </Button>
              )}
            </Stack>
          <Stack
            direction={{ base: 'column', md: 'column', lg: 'row'}}
            justifyContent="space-between"
            spacing="4"
          >
           <Stack mt="2">
              <ContactFilter
                value={contactFilter}
                onChange={setContactFilter}
                isdisabled={!!isCombinedView}
              />
          </Stack>
      

            <Stack spacing="4" direction={{ base: 'column', md: 'row', lg: 'row' }}>
              
            {isIndividualView && !archivedOnly && (
            <Box flex="1" textAlign="right">
              <Stack direction="row" spacing="3" justifyContent="flex-end">
                <BulkInviteButton
                  selection={selection}
                  queryState={queryState}
                  isLoadingSearch={isLoading}
                />
              </Stack>
            </Box>)}
            <Stack direction="row" spacing="4" justifyContent="flex-end">
              {archivedOnly && onOpenDeactivatedLogs && isIndividualView && (
                <Button
                  colorScheme="blue"
                  size="md"
                  fontWeight="semibold"
                  onClick={onOpenDeactivatedLogs}
                >
                  View Archived Logs
                </Button>
              )}
              {archivedOnly && onOpenArchivedOrganizationLogs && isOrganizationView && (
                <Button
                  colorScheme="blue"
                  size="md"
                  fontWeight="semibold"
                  onClick={onOpenArchivedOrganizationLogs}
                >
                  View Archived Logs
                </Button>
              )}
              {isIndividualView && (
            <Tooltip label={archivedOnly ? 'Export archived contacts' : 'Export individual contacts'}>
              <Box display="inline-block">
                <UserSearchExportButton
                  queryState={queryState}
                  archivedOnly={archivedOnly}
                />
              </Box>
            </Tooltip>
              )}

              {isOrganizationView && (
                <Tooltip label={archivedOnly ? 'Export archived organizations' : 'Export organizations'}>
                  <Box display="inline-block">
                    <OrganizationSearchExportButton
                      input={{
                        filter: input.filter,
                      }}
                    />
                  </Box>
                </Tooltip>
              )}

              {isIndividualView && !archivedOnly && (
                <Tooltip label="Import individual contacts">
                  <Box display="inline-block">
                    <MembersBulkImportButton />
                  </Box>
                </Tooltip>
              )}

              {isIndividualView && !archivedOnly && (
                <Tooltip label="Archive contacts via CSV">
                  <Box display="inline-block">
                    <BulkArchiveCsvButton />
                  </Box>
                </Tooltip>
              )}

              {isOrganizationView && !archivedOnly && (
                <Tooltip label="Import organizations">
                  <Box display="inline-block">
                    <OrganizationsBulkImportButton />
                  </Box>
                </Tooltip>
              )}

              {isOrganizationView && !archivedOnly && (
                <Tooltip label="Archive organizations via CSV">
                  <Box display="inline-block">
                    <BulkArchiveOrganizationsCsvButton />
                  </Box>
                </Tooltip>
              )}

              {!archivedOnly && (
              <Box display="flex" justifyContent="flex-end" mb="6">
                  <Button onClick={addContactModal.onOpen}>
                    <PlusIcon mr="1" boxSize={5} />
                    Add Contact
                  </Button>
              </Box>
              )}
              </Stack>

            </Stack>
          </Stack>
          {!isCombinedView && showFilters && !archivedOnly && isIndividualView && (
          <Stack direction="row" spacing="4" alignItems="flex-end">
            {memberManagement.isActive && (
              <>
                <MembershipStatusFilter
                  width="3xs"
                  value={queryState.membershipStatus}
                  onChange={(val) => dispatch({ membershipStatus: val })}
                />
                <MembershipType
                  width="3xs"
                  value={queryState.membershipType}
                  onChange={(val) => dispatch({ membershipType: val })}
                />
                <TermAlertsFilter
                  width="3xs"
                  value={queryState.termAlerts}
                  onChange={(val) => dispatch({ termAlerts: val })}
                />
              </>
            )}
            {!memberManagement.isActive && (
              <MembershipStatusFilter
                width="3xs"
                value={queryState.membershipStatus}
                onChange={(val) => dispatch({ membershipStatus: val })}
              />
            )}
            <AccountStatus
              width="3xs"
              value={queryState.accountStatus}
              onChange={(val) => dispatch({ accountStatus: val })}
            />
            <Role
              width="3xs"
              value={queryState.role}
              onChange={(val) => dispatch({ role: val })}
            />
            
          </Stack>)}
          {!isCombinedView && showFilters && !archivedOnly && isOrganizationView && (<>
           <Stack direction="row" spacing="4">
              {memberManagement.isActive && (
                <MembershipStatusFilter
                  width="3xs"
                  value={input.filter?.membershipStatuses ?? []}
                  onChange={(value) =>
                    setInput({
                      filter: { membershipStatuses: value },
                      offset: 0,
                    })
                  }
                />
              )}

              {memberManagement.isActive && (
                <MembershipTypeFilter
                  includeIndividualMemberships={false}
                  width="3xs"
                  value={input.filter?.membershipTypeNames ?? []}
                  onChange={(value) =>
                    setInput({
                      filter: { membershipTypeNames: value },
                      offset: 0,
                    })
                  }
                />
              )}

              {memberManagement.isActive && (
                <TermAlertsFilter
                  width="3xs"
                  value={input.filter?.termAlerts ?? []}
                  onChange={(value) =>
                    setInput({
                      filter: { termAlerts: value },
                      offset: 0,
                    })
                  }
                />
              )}

              <FormControl maxWidth="3xs">
                <FormLabel textStyle="detailStrong">
                  Organization Type
                </FormLabel>
                <Menu>
                  <MenuButton as={SelectButton} variant="menu">
                    <Text noOfLines={1}>
                      {input.filter?.isSponsor === true
                        ? 'Sponsor'
                        : input.filter?.isSponsor === false
                        ? 'Record'
                        : 'All'}
                    </Text>
                  </MenuButton>
                  <MenuList>
                    <MenuOptionGroup
                      onChange={(value: string) =>
                        setInput({
                          filter: {
                            isSponsor:
                              value === COMPANY_TYPE_FILTER.SPONSOR
                                ? true
                                : value === COMPANY_TYPE_FILTER.RECORD
                                ? false
                                : undefined,
                          },
                          offset: 0,
                        })
                      }
                      type="radio"
                    >
                      <MenuItemOption value={COMPANY_TYPE_FILTER.ALL}>
                        All
                      </MenuItemOption>
                      <MenuItemOption value={COMPANY_TYPE_FILTER.SPONSOR}>
                        Sponsor
                      </MenuItemOption>
                      <MenuItemOption value={COMPANY_TYPE_FILTER.RECORD}>
                        Record
                      </MenuItemOption>
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </FormControl>

              <FormControl maxWidth="3xs">
                <FormLabel textStyle="detailStrong">
                  Organization Status
                </FormLabel>
                <Menu>
                  <MenuButton as={SelectButton} variant="menu">
                    <Text noOfLines={1}>
                      {input.filter?.hidden === true
                        ? 'Hidden'
                        : input.filter?.hidden === false
                        ? 'Active'
                        : 'All'}
                    </Text>
                  </MenuButton>
                  <MenuList>
                    <MenuOptionGroup
                      onChange={(value: string) =>
                        setInput({
                          filter: {
                            hidden:
                              value === COMPANY_STATUS_FILTER.HIDDEN
                                ? true
                                : value === COMPANY_STATUS_FILTER.ACTIVE
                                ? false
                                : undefined,
                          },
                          offset: 0,
                        })
                      }
                      type="radio"
                    >
                      <MenuItemOption value={COMPANY_STATUS_FILTER.ALL}>
                        All
                      </MenuItemOption>
                      <MenuItemOption
                        value={COMPANY_STATUS_FILTER.ACTIVE}
                      >
                        Active
                      </MenuItemOption>
                      <MenuItemOption
                        value={COMPANY_STATUS_FILTER.HIDDEN}
                      >
                        Hidden
                      </MenuItemOption>
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </FormControl>
            </Stack>
          </>)}
        </Stack> 
      </Card>

      <Card overflow="hidden">
        <TableContainer>
          {isCombinedView && (
            <Table>
              <Thead>
                <Tr>
                  <Th pr="2" pl="2" width={10} />
                  <Th pr="2" pl="2">Name</Th>
                  <Th pr="2" pl="2">Contact</Th>
                  <Th pr="2" pl="2">
                    {memberManagement.isActive ? 'Membership' : 'Member'} Status
                  </Th>
                  {memberManagement.isActive && (
                    <Th pr="2" pl="2">Membership Type</Th>
                  )}
                  <Th pr="2" pl="2">Account Status</Th>
                  <Th pr="2" pl="2">Role</Th>
                  <Th pr="2" pl="2" w="60px" minW="60px" maxW="60px" boxShadow="-8px 0 12px -12px rgba(0,0,0,0.35)" position="sticky" right={0} zIndex={3} bg="#e7f1fa">
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {isCurrentLoading ? (
                  <MemberPlaceholders length={limit} />
                ) : (
                  <>
                    {combinedResults.map((item) => (
                      <CombinedSearchRow
                        key={`${item.type}-${item.data._id}`}
                        type={item.type}
                        data={item.data}
                        isExpanded={expandedRows.has(getRowId(item.type, item.data._id))}
                        onToggleExpand={() => toggleRow(getRowId(item.type, item.data._id))}
                        archivedOnly={archivedOnly}
                      />
                    ))}
                  </>
                )}
              </Tbody>
            </Table>
          )}

          {!isCombinedView && isIndividualView && (
            <Table>
              <Thead>
                <Tr>
                  <Th pr="2" pl="2" width={10}>
                    <HStack spacing="2">
                      <Checkbox
                        bg="white"
                        key="select-all-members"
                        isChecked={headerIsChecked}
                        isIndeterminate={headerIsIndeterminate}
                        onChange={handleSelectAllChange}
                      />
                    </HStack>
                  </Th>
                  <Th pr="2" pl="2">Name</Th>
                  <Th pr="2" pl="2">Contact Number</Th>
                  <Th pr="2" pl="2">
                    {memberManagement.isActive ? 'Membership' : 'Member'} Status
                  </Th>
                  {memberManagement.isActive && (
                    <Th pr="2" pl="2">Membership Type</Th>
                  )}
                  <Th pr="2" pl="2">Account Status</Th>
                  <Th pr="2" pl="2">Role</Th>
                  <Th pr="2" pl="2" w="60px"minW="60px"maxW="60px" boxShadow="-8px 0 12px -12px rgba(0,0,0,0.35)" position="sticky" right={0} zIndex={3} bg="#e7f1fa">
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {isCurrentLoading ? null : (
                <SelectionDescription
                  members={visibleMembers}
                  selection={selection}
                  selectAll={selectAll}
                  selectMany={selectMany}
                  colSpan={100}
                  pageSize={visibleMembers.length}
                  totalCount={
                    isCountLoading ? visibleMembers.length : membersTotalCount
                  }
                  numPages={Math.ceil(
                    (isCountLoading ? visibleMembers.length : membersTotalCount) /
                      limit
                  )}
                />)}

                {isCurrentLoading ? (
                  <MemberPlaceholders length={limit} />
                ) : (
                  <>
                    {visibleMembers.map((member) => (
                      <Member
                        key={member._id}
                        member={member}
                        selection={selection}
                        toggleOne={toggleOne}
                        isExpanded={expandedRows.has(getRowId('member', member._id))}
                        onToggleExpand={() => toggleRow(getRowId('member', member._id))}
                        archivedOnly={archivedOnly}
                      />
                    ))}
                  </>
                )}
              </Tbody>
            </Table>
          )}

          {!isCombinedView && isOrganizationView && (
            <Table>
              <Thead>
                <Tr>
                  <Th pr="2" pl="2" width={10}>
                    <HStack spacing="2">
                      <Checkbox
                        bg="white"
                        key="select-all-organizations"
                        isChecked={headerIsChecked}
                        isIndeterminate={headerIsIndeterminate}
                        onChange={handleSelectAllChange}
                      />
                    </HStack>
                  </Th>
                  <Th pr="2" pl="2">Organization Name</Th>
                  <Th pr="2" pl="2">Contact</Th>
                   <Th pr="2" pl="2">
                    {memberManagement.isActive ? 'Membership' : 'Member'} Status
                  </Th>
                  {memberManagement.isActive && (
                    <Th pr="2" pl="2">Membership Type</Th>
                  )}
                  <Th pr="2" pl="2">Account Status</Th>
                  <Th pr="2" pl="2">Role</Th>
                  <Th pr="2" pl="2" w="60px"minW="60px"maxW="60px" boxShadow="-8px 0 12px -12px rgba(0,0,0,0.35)" position="sticky" right={0} zIndex={3} bg="#e7f1fa" >
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                 {isCurrentLoading ? null : (
                <SelectionDescription
                  members={visibleOrganizations as any}
                  selection={orgSelection as any}
                  selectAll={orgSelectAll}
                  selectMany={orgSelectMany as any}
                  colSpan={100}
                  pageSize={visibleOrganizations.length}
                  totalCount={orgPagination.totalCount ?? visibleOrganizations.length}
                  numPages={organizationNumPages}
                />)}
                {isCurrentLoading ? (
                  <MemberPlaceholders length={limit} />
                ) : (
                  <>
                    {visibleOrganizations.map((org) => (
                      <OrganizationRow
                        key={org._id}
                        organization={org}
                        selection={orgSelection as any}
                        toggleOne={orgToggleOne as any}
                        isExpanded={expandedRows.has(getRowId('organization', org._id))}
                        onToggleExpand={() => toggleRow(getRowId('organization', org._id))}
                        colSpan={8}
                        archivedOnly={archivedOnly}
                      />
                    ))}
                  </>
                )}
              </Tbody>
            </Table>
          )}

          {!isCurrentLoading && displayTotalCount > 0 && (
            <Box py="4">
              {isCurrentFetchingMore && (
                <Center>
                  <Spinner size="sm" />
                </Center>
              )}

              {hasCurrentMore && <Box ref={loadMoreRef} h="1px" />}

              {!hasCurrentMore && (
                <Center fontSize="sm" color="gray.500">
                  No more results
                </Center>
              )}
            </Box>
          )}
        </TableContainer>

        {!isCurrentLoading &&
          !(isIndividualView && isCountLoading) &&
          displayTotalCount === 0 && (
          <DefaultNoSearchResults hasSearchQuery={!!queryState.searchText} />
        )}
      </Card>


        <NewAddContactModalMember
          isOpen={addContactModal.isOpen}
          onClose={addContactModal.onClose}
          showStepper
          flow={memberManagement.isActive ? 'memberManagement' : 'newUser'}
        />
    </Stack>
  );
};